import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  Pagination,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Header1 from "../Header/Header1";

const API_URL = process.env.REACT_APP_API_URL;

/* --------------------- ★ Modern Styled Components ★ --------------------- */

const NeonCard = styled(Paper)(() => ({
  background: "rgba(255,255,255,0.05)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  border: "1px solid rgba(255,255,255,0.1)",
  padding: "20px",
  transition: "0.25s ease",
  color: "#fff",
  "&:hover": {
    transform: "translateY(-6px)",
    borderColor: "#00eaff",
    boxShadow: "0 0 25px rgba(0, 191, 255, 0.4)",
  },
}));

const SearchField = styled(TextField)(() => ({
  input: {
    color: "#fff",
    background: "#0f0f0f",
    borderRadius: "8px",
    padding: "10px",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#333" },
    "&:hover fieldset": { borderColor: "#777" },
    "&.Mui-focused fieldset": { borderColor: "#00eaff" },
  },
}));

/* ------------------------------------------------------------------------ */

function CustomerQueriesResolve({ userId, onLogout }) {
  const [supportQueries, setSupportQueries] = useState([]);
  const [filteredQueries, setFilteredQueries] = useState([]);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [resolveError, setResolveError] = useState(null);

  const searchRef = useRef(null);

  /* ---------------- Fetch Queries ---------------- */
  useEffect(() => {
    const fetchSupportQueries = async () => {
      try {
        const response = await fetch(`${API_URL}/list_support_query/`);
        const data = await response.json();
        setSupportQueries(data);
        setFilteredQueries(data);
      } catch (error) {
        console.error("Error fetching support queries:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSupportQueries();
  }, []);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, [loading]);

  /* ---------------- Filtering ---------------- */
  useEffect(() => {
    const results = supportQueries.filter((q) =>
      `${q.query} ${q.email} ${q.status}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredQueries(results);
    setCurrentPage(1);
  }, [search, supportQueries]);

  /* ---------------- Pagination ---------------- */
  const pageCount = Math.ceil(filteredQueries.length / rowsPerPage);
  const paginatedData = filteredQueries.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  /* ---------------- Resolve Query ---------------- */
  const handleResolve = async (queryId) => {
    setResolveError(null);

    try {
      const response = await fetch(`${API_URL}/update_support_query/${queryId}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_resolved: true }),
      });

      if (response.ok) {
        setSupportQueries((prev) =>
          prev.map((query) =>
            query.id === queryId ? { ...query, status: "resolved" } : query
          )
        );
      } else {
        setResolveError("Failed to resolve the query.");
      }
    } catch (error) {
      console.error("Error resolving query:", error);
      setResolveError("An error occurred while resolving the query.");
    }
  };

  /* ------------------------------------------------------------------------ */

  return (
    <div>
      <Header1 userId={userId} onLogout={onLogout} />

      <Box
        sx={{
          backgroundColor: "#000000ff",
          minHeight: "100vh",
          pt: 12,
          pb: 6,
          px: { xs: 2, md: 8 },
          color: "#fff",
        }}
      >
        {/* ---------------- Title ---------------- */}
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 4,
            letterSpacing: 1.5,
            background: "linear-gradient(90deg,#00eaff,#ff00ff)",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Customer Support Queries
        </Typography>

        {/* ---------------- Filters Row ---------------- */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 4, gap: 2 }}
        >
          <Chip
            label={`Total: ${supportQueries.length} | Matching: ${filteredQueries.length}`}
            sx={{
              background: "rgba(0,255,255,0.15)",
              color: "#00eaff",
              px: 2,
              py: 1,
              fontWeight: "bold",
            }}
          />

          <Box display="flex" gap={2} flexWrap="wrap">
            <SearchField
              placeholder="Search query, email, status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputRef={searchRef}
              size="small"
              sx={{ width: { xs: "100%", sm: "280px" } }}
            />

            <Select
              value={rowsPerPage}
              size="small"
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              sx={{
                color: "#fff",
                background: "#0f0f0f",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#333" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#777" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#00eaff",
                },
              }}
            >
              {[6, 12, 18].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}/page
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* ---------------- Error ---------------- */}
        {resolveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {resolveError}
          </Alert>
        )}

        {/* ---------------- Loading ---------------- */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress sx={{ color: "#00eaff" }} />
          </Box>
        ) : filteredQueries.length === 0 ? (
          <Typography align="center" sx={{ mt: 4, color: "#bbb" }}>
            No support queries found.
          </Typography>
        ) : (
          <>
            {/* ---------------- Cards Grid ---------------- */}
            <Grid container spacing={3}>
              {paginatedData.map((query) => (
                <Grid item xs={12} sm={6} md={4} key={query.id}>
                  <NeonCard elevation={0}>
                    <Typography sx={{ mb: 1, color: "#00eaff", fontWeight: "bold" }}>
                      Query ID: {query.id}
                    </Typography>

                    <Typography sx={{ mb: 1 }}>
                      <strong>Query:</strong> {query.query}
                    </Typography>

                    <Typography sx={{ mb: 1 }}>
                      <strong>Email:</strong> {query.email}
                    </Typography>

                    <Typography sx={{ mb: 1 }}>
                      <strong>Submitted:</strong>{" "}
                      {new Date(query.submitted_at).toLocaleString()}
                    </Typography>

                    {query.status === "resolved" ? (
                      <Chip
                        label="Resolved"
                        color="success"
                        sx={{
                          mt: 2,
                          fontWeight: "bold",
                          backgroundColor: "#1faa00",
                          color: "#fff",
                        }}
                      />
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        sx={{
                          mt: 2,
                          textTransform: "none",
                          backgroundColor: "#1faa00",
                          "&:hover": { backgroundColor: "#178a00" },
                        }}
                        onClick={() => handleResolve(query.id)}
                      >
                        Resolve Query
                      </Button>
                    )}
                  </NeonCard>
                </Grid>
              ))}
            </Grid>

            {/* ---------------- Pagination ---------------- */}
            {pageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(e, val) => setCurrentPage(val)}
                  size="large"
                  sx={{
                    "& .MuiPaginationItem-root": { color: "#00eaff" },
                    "& .Mui-selected": {
                      background:
                        "linear-gradient(90deg,#00eaff,#ff00ff) !important",
                      color: "#000 !important",
                      boxShadow: "0 0 10px #00eaff",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </div>
  );
}

export default CustomerQueriesResolve;
